import { Card, CardContent } from "@elit-hair/elit-ui";
import { useTranslation } from "react-i18next";

import { CallButton } from "@/components/ui/call-button";
import { Dialpad } from "@/pages/home/components/dial-panel/dialpad";
import { PhoneNumberInput } from "@/pages/home/components/dial-panel/phone-number-input";
import { CALL_PURPOSES } from "@/pages/performance/quality-watch/utils/quality-watch.constants";
import { useCall } from "@/providers/hooks/use-call";
import { PermissionNames, usePermissions } from "@/providers/hooks/use-permissions";
import { CallStatus } from "@/types/call.types";

type DialerContentProps = {
    calledPhoneNumber: string;
    setCalledPhoneNumber: (value: string) => void;
    handleHangup: () => void;
    handleNumberClick: (num: string) => void;
    callStatus: CallStatus;
};

export const DialerContent = ({
    calledPhoneNumber,
    setCalledPhoneNumber,
    handleHangup,
    handleNumberClick,
    callStatus,
}: DialerContentProps) => {
    const { startCall, initialized } = useCall();
    const { validatePermissions } = usePermissions();
    const { t } = useTranslation();

    const call = async (number: string) => {
        try {
            await startCall(number, {
                callPurpose: CALL_PURPOSES.MANUAL,
            });
        } catch (error) {
            console.error("Failed to start call:", error);
        }
    };

    return (
        <Card className="h-full w-full border-none bg-transparent">
            <CardContent className="my-4 flex w-full flex-col gap-2 p-0">
                <PhoneNumberInput
                    value={calledPhoneNumber}
                    onChange={setCalledPhoneNumber}
                    onEnterPress={() => call(calledPhoneNumber)}
                />
                <Dialpad onNumberClick={handleNumberClick} callStatus={callStatus} />
                <CallButton
                    className="w-full"
                    onCall={() => {
                        if (validatePermissions(PermissionNames.MICROPHONE)) {
                            call(calledPhoneNumber);
                        }
                    }}
                    onHangup={handleHangup}
                    disabled={!calledPhoneNumber.length || !initialized}
                    callStatus={callStatus}
                />

                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-2.5 py-2">
                    <p className="text-xs leading-snug text-destructive">{t("manual_dialer_warning_description")}</p>
                </div>
            </CardContent>
        </Card>
    );
};
